import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { mainSiteUrl } from "@/mocks/categories";
import { useEffect } from "react";

interface ArticleLayoutProps {
  title: string;
  description: string;
  date: string;
  readTime: string;
  tags: string[];
  imageUrl: string;
  imageAlt: string;
  dateISO: string;
  slug: string;
  children: React.ReactNode;
}

function buildArticleJsonLd(
  title: string,
  description: string,
  imageUrl: string,
  dateISO: string,
  slug: string
): string {
  const pageUrl = `https://kakobuypolska.fun/artykul/${slug}`;
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description: description,
    image: imageUrl,
    datePublished: dateISO,
    dateModified: dateISO,
    author: {
      "@type": "Organization",
      name: "Kakobuy Polska",
      url: "https://kakobuypolska.fun/",
    },
    publisher: {
      "@type": "Organization",
      name: "Kakobuy Polska",
      url: "https://kakobuypolska.fun/",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageUrl,
    },
  });
}

export default function ArticleLayout({
  title,
  description,
  date,
  readTime,
  tags,
  imageUrl,
  imageAlt,
  dateISO,
  slug,
  children
}: ArticleLayoutProps) {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: buildArticleJsonLd(title, description, imageUrl, dateISO, slug),
        }}
      />
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md shadow-sm">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">
                Kakobuy <span className="text-primary-600">Polska</span>
              </span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors font-medium cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-4-line"></i>
              </div>
              <span className="hidden sm:inline">{t("nav.home")}</span>
            </Link>
          </div>
        </div>
      </nav>

      <article className="min-h-screen bg-background-50 pt-16">
        <header className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-14 md:py-18">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium mb-6 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              {t("article.page.back")}
            </Link>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              {description}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-6">
              <span className="text-xs text-foreground-500">Opublikowano: {date}</span>
              <span className="text-xs text-foreground-500">•</span>
              <span className="text-xs text-foreground-500">Czas czytania: {readTime}</span>
              <span className="text-xs text-foreground-500">•</span>
              <span className="text-xs text-foreground-500">Autor: Kakobuy Polska</span>
            </div>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="rounded-2xl overflow-hidden aspect-[16/7] mb-10 bg-background-100">
            <img
              src={imageUrl}
              alt={imageAlt}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose prose-sm md:prose-base max-w-none">
            {children}
          </div>

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-6">
              <span>Tagi:</span>
              {tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 bg-background-100 rounded-full">{tag}</span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Sprawdź Kakobuy Spreadsheet
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-semibold rounded-lg text-sm transition-all duration-200 border border-background-300/60 whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-4-line"></i>
                </div>
                Wróć do strony głównej
              </Link>
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-secondary-900 text-background-50 py-8">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-background-50/70 hover:text-background-50 transition-colors mb-4 cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            {t("article.page.back")}
          </Link>
          <p className="text-xs text-background-50/50">
            © 2026 Kakobuy Polska. Wszelkie prawa zastrzeżone. Ta strona jest niezależnym przewodnikiem.
          </p>
        </div>
      </footer>
    </>
  );
}